import {RestApiService} from '../../services/rest-api.service';
import {L10nTranslationService} from 'angular-l10n';
import {
    ActionColumn,
    BaseCollectionModel,
    BaseController,
    CheckColumnSettings,
    ColumnSettings,
    ComboboxItem,
    DateColumnSettings,
    ExpandedStart,
    NumberColumnSettings,
    SearchModel,
    TableSettings,
    TextColumnSettings
} from '@yasc/yasc-material-components';
import {Injectable} from '@angular/core';
import {UserDetailsRes, UserUpdateReq} from '../../contracts/v0/cp';
import {RuntimeCacheService} from '../../services/runtime-cache.service';
import {MatDialog} from '@angular/material/dialog';
import {BehaviorSubject, map} from 'rxjs';
import {RequestCacheService} from '../../services/request-cache-service';
import {ProjectMinRes} from '../../contracts/v0/crm';
import {PermissionList} from '../../permissions/permission-list';

@Injectable({
    providedIn: 'root'
})
export class UserController extends BaseController<UserDetailsRes, UserUpdateReq> {

    userComboboxItemList: ComboboxItem[] = [];
    userSearchLoaded$ = new BehaviorSubject<boolean>(false);

    constructor(
        private translate: L10nTranslationService,
        private restApiService: RestApiService,
        private runtimeCache: RuntimeCacheService,
        private requestCacheService: RequestCacheService,
        matDialog: MatDialog,
    ) {
        super();
        // @ts-ignore
        this.dialog = matDialog
        this.runTimeCache = runtimeCache
        this.yascDataSource.Settings = this.getTableSettings();
        this.yascDataSource.Settings.ColumnSettings = this.getColumnSettings();
    }


    override getTableSettings(): TableSettings<UserDetailsRes> {
        return new TableSettings<UserDetailsRes>({
            yascShowColumnsMenu: true,
            yascStickyHeader: true,
            yascMultiSelect: true,
            yascInlineEditing: false,
            yascRowClick: false,
            yascActionColumns: [
                ActionColumn.Show,
                {
                    action: ActionColumn.Edit,
                    displayIf: () => this.runtimeCache.hasPermission(PermissionList.AccountUpdateId)
                },
                {
                    action: ActionColumn.Delete,
                    displayIf: (user: UserDetailsRes) =>
                        this.runtimeCache.hasPermission(PermissionList.AccountDeleteId)
                        && this.runtimeCache.loginInfo?.id !== user.id
                }
            ],
            yascStartExpanded: ExpandedStart.AllExpanded,
            yascLabel: this.translate.translate('navUsers'),
        });
    }

    override getColumnSettings(): ColumnSettings<UserDetailsRes>[] {
        return [
            new NumberColumnSettings('id', {
                yascLabel: this.translate.translate('generalId'),
                yascWidth: 1,
            }),
            new TextColumnSettings('email', {
                yascLabel: this.translate.translate('employeeEmail'),
                yascWidth: 3,
            }),
            new CheckColumnSettings('emailConfirmed', {
                yascLabel: this.translate.translate('employeeVerified'),
                yascReadonly: true,
            }),
            new CheckColumnSettings('isActive', {
                yascLabel: this.translate.translate('generalActive'),
                yascReadonly: true,
            }),
            new NumberColumnSettings('permissions', {
                yascLabel: this.translate.translate('generalPermissions'),
                yascDisplayFn: (user: UserDetailsRes) => user.permissions.length,
            }),
            new NumberColumnSettings('rollen', {
                yascLabel: this.translate.translate('generalRoles'),
                yascDisplayFn: (user: UserDetailsRes) => user.roles.length,
            }),
            new DateColumnSettings('lastPasswordUpdate', {
                yascLabel: this.translate.translate('employeeLastPasswordChange'),
            }),
            new DateColumnSettings('created', {
                yascLabel: this.translate.translate('generalCreated'),
            }),
        ];
    }

    override loadData(): Promise<BaseCollectionModel<UserDetailsRes>> {
        return this.requestCacheService.getCached<BaseCollectionModel<UserDetailsRes>>({
            key: 'core.users.max',
            loader: () => this.restApiService.v0.accounts.search(),
            onCached: (val) => {
                this.applyUsers(val.data)
                this.userSearchLoaded$.next(true);
            },
            onSuccess: (val) => {
                this.applyUsers(val.data)
                this.userSearchLoaded$.next(true);
            },
            onError: () => {
                this.userSearchLoaded$.next(false);
            }
        });

    }

    loadAdministrationData(): Promise<BaseCollectionModel<UserDetailsRes>> {
        return this.requestCacheService.getCached<BaseCollectionModel<UserDetailsRes>>({
            key: 'admin.users.max',
            forceReload: true,
            loader: () => this.restApiService.v0.administration.accounts.search(),
            onCached: (val) => {
                this.applyUsers(val.data)
                this.userSearchLoaded$.next(true);
            },
            onSuccess: (val) => {
                this.applyUsers(val.data)
                this.userSearchLoaded$.next(true);
            },
            onError: () => {
                this.userSearchLoaded$.next(false);
            }
        });
    }

    override update(req?: UserUpdateReq) {
        req = req ?? this.currentCreateItem;
        return this.restApiService.v0.accounts.update(req!).subscribe({
            next: (val) => {
                this.currentItem = val
                this.currentCreateItem = new UserUpdateReq(val)
            }
        });

    }

    override onCopy(): void {
        throw new Error('Not implemented!');
    }

    override onEdit(val?: UserUpdateReq | undefined): void {
        throw new Error("Method not implemented.");
    }

    override onCreate(val?: UserUpdateReq | undefined): void {
        throw new Error("Method not implemented.");
    }

    override onBulkUpdate(val: UserUpdateReq[]): void {
        throw new Error("Method not implemented.");
    }

    override toUpdateRequest(val: UserDetailsRes): UserUpdateReq {
        throw new Error("Method not implemented.");
    }

    override toCreateRequest(val: UserDetailsRes): UserUpdateReq {
        throw new Error("Method not implemented.");
    }

    override onCancel(): void {
        throw new Error("Method not implemented.");
    }

    override onSave(): void {
        throw new Error("Method not implemented.");
    }

    override onDelete(): void {
        throw new Error("Method not implemented.");
    }

    override onBulkPost(): void {
        throw new Error("Method not implemented.");
    }


    private applyUsers(data: UserDetailsRes[]) {
        this.yascDataSource.TableData = data;
        this.userComboboxItemList = data.map(e => new ComboboxItem(`${e.givenName} ${e.lastName}`, e))
        return data;
    }
}
